import React, { useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { MultiSelect, MultiSelectChangeEvent } from '@progress/kendo-react-dropdowns';
import { plusIcon } from '@progress/kendo-svg-icons';
import type { EntityNoteCategory, EntityNoteInput } from './EntityNotesPanel';
import type { CategoryOption } from './noteHooks';
import NoteDescriptionInput from './NoteDescriptionInput';
import useIsGbUser from '../../../../Identity/Utilities/useIsGbUser';

interface Props {
  categories: CategoryOption[];
  onAdd: (note: EntityNoteInput) => void;
  renderCategoryItem: (li: React.ReactElement, itemProps: any) => React.ReactElement;
  labels: {
    title: string;
    category: string;
    categoryPlaceholder: string;
    description: string;
    addNoteButton: string;
    addNotePlaceholder: string;
  };
}

export default function EntityNoteForm({
  categories,
  onAdd,
  renderCategoryItem,
  labels,
}: Props) {
  const [description, setDescription] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<CategoryOption[]>([]);
  const [internalOnly, setInternalOnly] = useState(false);
  const isGbUser = useIsGbUser();

  const canSubmit = description.trim().length > 0 && selectedCategories.length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onAdd({
      description: description.trim(),
      categories: selectedCategories.map((c) => c.value as EntityNoteCategory),
      isInternalOnly: internalOnly,
    });
    setDescription('');
    setSelectedCategories([]);
    setInternalOnly(false);
  };

  return (
    <div className="shrink-0 border-b border-gray-200 px-4 py-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{labels.title}</div>
      <div className="mb-3">
        <label className="mb-1 block text-sm">{labels.category}</label>
        <MultiSelect
          data={categories}
          textField="text"
          dataItemKey="value"
          value={selectedCategories}
          onChange={(e: MultiSelectChangeEvent) => setSelectedCategories(e.value)}
          itemRender={renderCategoryItem}
          placeholder={labels.categoryPlaceholder}
          autoClose={false}
          popupSettings={{ className: 'my-multiselect-popup notes-drawer-popup' }}
          size="medium"
          style={{ width: '100%', overflow: 'auto', minHeight: 30 }}
        />
      </div>
      <div className="mb-3">
        <label className="mb-1 block text-sm">{labels.description}</label>
        <NoteDescriptionInput
          value={description}
          onChange={setDescription}
          placeholder={labels.addNotePlaceholder}
          rows={4}
          showInternalToggle={isGbUser}
          internalOnly={internalOnly}
          onInternalOnlyChange={setInternalOnly}
        />
      </div>
      <div className="flex justify-end">
        <Button themeColor="primary" svgIcon={plusIcon} disabled={!canSubmit} onClick={handleSubmit}>
          {labels.addNoteButton}
        </Button>
      </div>
    </div>
  );
}
